// react
import { Suspense, useState } from 'react';
import type { ReactElement } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { Link } from '@tanstack/react-router';

// material ui icons
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded';
import OpenInFullRoundedIcon from '@mui/icons-material/OpenInFullRounded';

// material ui
import { Box, IconButton, Skeleton, Tooltip } from '@mui/material';

// api types
import type { TimeSeriesDataPoint } from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysis';

// shared components
// feature-components
import EnergyKpiCard from '@/features/energyxplorer/components/portfolio-dashboard/EnergyKpiCard';
import {
  PortfolioKpiSparkline,
  PortfolioKpiSparklineDialog,
} from '@/features/energyxplorer/components/portfolio-dashboard/PortfolioKpiSparkline';

interface PortfolioAnalysisServiceCardProps {
  disabled?: boolean;
  name?: string;
  label?: string;
  path?: string;
  value?: number;
  trend?: number;
  unit?: string;
  icon: ReactElement;
  color: string;
  summedData?: TimeSeriesDataPoint[];
}

const PortfolioAnalysisServiceCard = (
  props: PortfolioAnalysisServiceCardProps,
) => {
  const {
    disabled = false,
    name,
    label = name,
    path,
    value,
    trend,
    unit,
    icon,
    color,
    summedData,
  } = props;
  const { t } = useTranslation('portfolio-analysis_dashboard');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const canOpenDialog = Array.isArray(summedData) && summedData.length > 0;
  const labelText = label ?? name;
  const actions = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {canOpenDialog ? (
        <Tooltip title={t('actions.showChart')}>
          <IconButton
            size="small"
            aria-label={t('actions.showChart')}
            onClick={() => setIsDialogOpen(true)}
          >
            <OpenInFullRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}
      {path && !disabled ? (
        <Tooltip title={t('actions.openDetails')}>
          <IconButton
            size="small"
            aria-label={t('actions.openDetails')}
            component={Link}
            to={`${path}`}
          >
            <LaunchRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}
    </Box>
  );

  return (
    <Suspense fallback={<Skeleton />}>
      <EnergyKpiCard
        title={labelText ?? ''}
        icon={icon}
        color={color}
        value={disabled ? undefined : value}
        unit={unit ?? ''}
        enabled={!disabled}
        unavailableText={t('cards.unavailable')}
        period={t('cards.period_last_12_months')}
        trend={trend}
        trendTooltip={t('cards.trend_tooltip')}
        actions={actions}
      >
        {canOpenDialog ? (
          <PortfolioKpiSparkline
            dataPoints={summedData}
            unit={unit}
            color={color}
          />
        ) : null}
      </EnergyKpiCard>
      <PortfolioKpiSparklineDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        dataPoints={summedData}
        unit={unit}
        color={color}
        label={labelText}
      />
    </Suspense>
  );
};

export default PortfolioAnalysisServiceCard;
